import {
  Matrix3,
  Matrix4,
  Quaternion,
  HeadingPitchRoll,
  Cartesian3,
  Transforms,
} from "@cesium/engine";

const computeMatrix = (position, rotation, scale) => {
  if (rotation instanceof Quaternion) {
    rotation = HeadingPitchRoll.fromQuaternion(rotation);
  }

  const transform = Transforms.headingPitchRollToFixedFrame(position, rotation);

  Matrix4.setScale(transform, scale, transform);

  return transform;
};

/**
 * Cesium Model Editing Class
 * @alias EditModel
 * @private
 */
class EditModel {
  /**
   * BoundingSphere 추출
   * @param {Model} object
   * @returns {BoundingSphere}
   */
  static getBoundingSphere(object) {
    const boundingSphere = object.boundingSphere;

    return boundingSphere;
  }

  /**
   * Transform 추출
   * @param {Model} object
   * @param {boolean} enu
   * @returns {object}
   */
  static decomposition(object, enu = true) {
    const matrix4 = object.modelMatrix;

    const translation = Matrix4.getTranslation(matrix4, new Cartesian3()); // translation
    const scale = Matrix4.getScale(matrix4, new Cartesian3()); // scale

    const position = translation;
    const originalPosition = translation;

    let quaternion;
    let headingPitchRoll;
    if (enu) {
      // ENU 회전 값 제거
      const inverseMatrix = Matrix4.inverse(
        Transforms.eastNorthUpToFixedFrame(position),
        new Matrix4()
      );
      const newMatirx4 = Matrix4.multiply(
        inverseMatrix,
        matrix4,
        new Matrix4()
      );

      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(newMatirx4, new Matrix3());

      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    } else {
      // 회전행렬 추출
      const matrix3 = Matrix4.getRotation(matrix4, new Matrix3());

      quaternion = Quaternion.fromRotationMatrix(matrix3, new Quaternion());
      headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        quaternion,
        new HeadingPitchRoll()
      );
    }

    const result = {
      position: position,
      originalPosition: originalPosition,
      translation: translation,
      quaternion: quaternion,
      headingPitchRoll: headingPitchRoll,
      scale: scale,
    };

    return result;
  }

  /**
   *
   * @param {Model} object
   * @returns {Model}
   */
  static reset(object) {
    const { originalPosition } = this.decomposition(object);

    const matrix = computeMatrix(
      originalPosition,
      Quaternion.IDENTITY,
      Cartesian3.ONE
    );

    object.modelMatrix = matrix;

    return object;
  }

  /**
   *
   * @param {Model} object
   * @param {Matrix4} matrix
   * @returns {Matrix4}
   */
  static setMatrix(object, matrix) {
    object.modelMatrix = matrix;

    return matrix;
  }

  /**
   *
   * @param {Model} object
   * @returns {Matrix4}
   */
  static getMatrix(object) {
    return object.modelMatrix;
  }

  /**
   *
   * @param {Matrix4} object
   * @param {Cartesian3} position
   * @returns {Cartesian3}
   */
  static setPosition(object, position) {
    const { quaternion, scale } = this.decomposition(object);

    const matrix = computeMatrix(position, quaternion, scale);

    object.modelMatrix = matrix;

    return position;
  }

  /**
   *
   * @param {Model} object
   * @returns {Cartesian3}
   */
  static getPosition(object) {
    const { position } = this.decomposition(object);

    return position;
  }

  /**
   *
   * @param {Model} object
   * @param {Quaternion | HeadingPitchRoll} rotation
   * @returns {Quaternion | HeadingPitchRoll}
   */
  static setRotation(object, rotation) {
    const { position, scale } = this.decomposition(object);

    const matrix = computeMatrix(position, rotation, scale);

    object.modelMatrix = matrix;

    return rotation;
  }

  /**
   *
   * @param {Model} object
   * @param {boolean} enu
   * @returns {Quaternion}
   */
  static getQuaternion(object, enu = true) {
    const { quaternion } = this.decomposition(object, enu);

    return quaternion;
  }

  /**
   *
   * @param {Model} object
   * @param {boolean} enu
   * @returns {HeadingPitchRoll}
   */
  static getHeadingPitchRoll(object, enu = true) {
    const { headingPitchRoll } = this.decomposition(object, enu);

    return headingPitchRoll;
  }

  /**
   *
   * @param {Model} object
   * @param {Cartesian3} scale
   * @returns {Cartesian3}
   */
  static setScale(object, scale) {
    const { position, quaternion } = this.decomposition(object);

    const matrix = computeMatrix(position, quaternion, scale);

    object.modelMatrix = matrix;

    return scale;
  }

  /**
   *
   * @param {Model} object
   * @returns {Cartesian3}
   */
  static getScale(object) {
    const { scale } = this.decomposition(object);

    return scale;
  }

  /**
   *
   * @param {Model} object
   * @param {Cartesian3} translation
   * @param {Quaternion | HeadingPitchRoll} rotation
   * @param {Cartesian3} scale
   * @returns {object}
   */
  static setTranslationRotationScale(object, translation, rotation, scale) {
    // HeadingPitchRoll => Quaternion
    if (rotation instanceof HeadingPitchRoll) {
      rotation = Quaternion.fromHeadingPitchRoll(rotation, new Quaternion());
    }

    // 원본 좌표
    const { originalPosition } = this.decomposition(object);

    // 원본 좌표 + translation
    const position = Cartesian3.add(
      originalPosition,
      translation,
      new Cartesian3()
    );

    // 메트릭스 계산
    const matrix = computeMatrix(position, rotation, scale);

    // set matrix
    object.modelMatrix = matrix;

    return {
      position: position,
      translation: translation,
      rotation: rotation,
      scale: scale,
    };
  }
}

export default EditModel;
